import { useEffect, useState } from 'react'
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'
import { SortableContext, arrayMove, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { SortableRow } from './SortableRow'
import type { SortableListProps } from './types'

export function SortableList<T>({
  items,
  getId,
  renderItem,
  onReorder,
  empty,
  className = 'flex flex-col gap-1.5',
}: SortableListProps<T>) {
  const [local, setLocal] = useState(items)
  useEffect(() => { setLocal(items) }, [items])

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }))
  const ids = local.map(getId)

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id) return
    const oldIdx = local.findIndex(i => getId(i) === active.id)
    const newIdx = local.findIndex(i => getId(i) === over.id)
    if (oldIdx === -1 || newIdx === -1) return
    const reordered = arrayMove(local, oldIdx, newIdx)
    setLocal(reordered)
    onReorder(reordered.map(getId))
  }

  if (local.length === 0 && empty !== undefined) {
    return <div className={className}>{empty}</div>
  }

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={ids} strategy={verticalListSortingStrategy}>
        <div className={className}>
          {local.map(item => (
            <SortableRow key={getId(item)} id={getId(item)}>
              {renderItem(item)}
            </SortableRow>
          ))}
        </div>
      </SortableContext>
    </DndContext>
  )
}
